import CircularProgress from '@mui/material/CircularProgress'
import clsx from 'clsx'
import { activityHooks } from 'features/activity'
import { notificationUtils } from 'features/notifications'
import { workspaceUtils } from 'features/workspace'
import { useId, useMemo, useState } from 'react'
import { notifyError } from 'shared/errors'
import { typo } from 'shared/styles'
import { CustomSelect } from 'shared/ui'

import { api } from '../../../api/api'
import { CONSTS } from '../../../config/objectConst'
import css from './DailyLimitsEditor.module.scss'

const showActivitySnack = () => notificationUtils.snackById('3.15')

interface Option {
	label: string
	value: string
}

interface TempStats {
	follow_limit?: number
	invitations_limit?: number
	messages_limit?: number
	react_on_post_limit?: number
}

const getInit = (options: Option[], value: TempStats[keyof TempStats]) => {
	value ??= 0

	return options.find(el => Number(el.value) === value)
}

export function DailyLimitsEditor({
	customStatistics
}: {
	customStatistics: TempStats
}) {
	const currentLimits = useMemo(
		() => ({
			followPerDayLimit: getInit(
				CONSTS.DAILY_LIMITS.FOLLOW,
				customStatistics.follow_limit
			),
			invitationPerDayLimit: getInit(
				CONSTS.DAILY_LIMITS.INVITATIONS,
				customStatistics.invitations_limit
			),
			likesPerDayLimit: getInit(
				CONSTS.DAILY_LIMITS.LIKES,
				customStatistics.react_on_post_limit
			),
			messagesPerDayLimit: getInit(
				CONSTS.DAILY_LIMITS.MESSAGES,
				customStatistics.messages_limit
			)
		}),
		[customStatistics]
	)

	const id = useId()

	const refetchActivity = activityHooks.useRefetch()

	const [loaderMessages, setLoaderMessages] = useState(false)
	const [loaderInvitation, setLoaderInvitation] = useState(false)
	const [loaderLikes, setLoaderLikes] = useState(false)
	const [loaderFollow, setLoaderFollow] = useState(false)

	const update = (path: Partial<typeof currentLimits>) =>
		api
			.setDailyLimits({ ...currentLimits, ...path })
			.then(() => {
				showActivitySnack()
				workspaceUtils.refetchStatistics()
			})
			.catch(notifyError)
	return (
		<div className={css.root}>
			<div className={css.row}>
				<label
					className={clsx(css.label, typo.buttonSm)}
					htmlFor={`${id}-invitation`}
				>
					Max invitations per day:
				</label>

				{currentLimits.invitationPerDayLimit && (
					<CustomSelect
						id={`${id}-invitation`}
						isDisabled={loaderInvitation}
						isSearchable={false}
						onChange={invitationPerDayLimit => {
							if (!invitationPerDayLimit) {
								return
							}
							setLoaderInvitation(true)

							update({ invitationPerDayLimit }).finally(() => {
								setLoaderInvitation(false)
								refetchActivity()
							})
						}}
						options={CONSTS.DAILY_LIMITS.INVITATIONS}
						value={currentLimits.invitationPerDayLimit}
					/>
				)}

				{loaderInvitation && (
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						className={css.loader}
						size={18}
					/>
				)}
			</div>

			<div className={css.row}>
				<label
					className={clsx(css.label, typo.buttonSm)}
					htmlFor={`${id}-messages`}
				>
					Max messages per day:
				</label>

				{currentLimits.messagesPerDayLimit && (
					<CustomSelect
						id={`${id}-messages`}
						isDisabled={loaderMessages}
						isSearchable={false}
						onChange={messagesPerDayLimit => {
							if (!messagesPerDayLimit) {
								return
							}
							setLoaderMessages(true)

							update({ messagesPerDayLimit }).finally(() => {
								setLoaderMessages(false)
								refetchActivity()
							})
						}}
						options={CONSTS.DAILY_LIMITS.MESSAGES}
						value={currentLimits.messagesPerDayLimit}
					/>
				)}

				{loaderMessages && (
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						className={css.loader}
						size={18}
					/>
				)}
			</div>

			<div className={css.row}>
				<label
					className={clsx(css.label, typo.buttonSm)}
					htmlFor={`${id}-liked`}
				>
					Max likes per day:
				</label>

				{currentLimits.likesPerDayLimit && (
					<CustomSelect
						id={`${id}-liked`}
						isDisabled={loaderLikes}
						isSearchable={false}
						onChange={likesPerDayLimit => {
							if (!likesPerDayLimit) {
								return
							}
							setLoaderLikes(true)

							update({ likesPerDayLimit }).finally(() => {
								setLoaderLikes(false)
								refetchActivity()
							})
						}}
						options={CONSTS.DAILY_LIMITS.LIKES}
						value={currentLimits.likesPerDayLimit}
					/>
				)}

				{loaderLikes && (
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						className={css.loader}
						size={18}
					/>
				)}
			</div>
			<div className={css.row}>
				<label
					className={clsx(css.label, typo.buttonSm)}
					htmlFor={`${id}-follows`}
				>
					Max following per day:
				</label>

				{currentLimits.followPerDayLimit && (
					<CustomSelect
						id={`${id}-follows`}
						isDisabled={loaderFollow}
						isSearchable={false}
						onChange={followPerDayLimit => {
							if (!followPerDayLimit) {
								return
							}
							setLoaderFollow(true)

							update({ followPerDayLimit }).finally(() => {
								setLoaderFollow(false)
								refetchActivity()
							})
						}}
						options={CONSTS.DAILY_LIMITS.FOLLOW}
						value={currentLimits.followPerDayLimit}
					/>
				)}

				{loaderFollow && (
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						className={css.loader}
						size={18}
					/>
				)}
			</div>
		</div>
	)
}
